/**
 * Firebase App Check, the parts the client and the server must agree on (MAR-1214).
 *
 * - `APP_CHECK_ROUTES`: the routes whose handlers call `verifyAppCheckToken`, and which kind of
 *   token each needs. The client (`app/plugins/api.ts`, `useApi().apiStreamFetch`) attaches a token
 *   to exactly these requests, so an ordinary page never loads App Check (and never pays a
 *   reCAPTCHA assessment). The one other place a token is needed, the first `users` row for a new
 *   account (`resolveUserContext`), is answered by a single retry on 401 `app_check_required`.
 * - `appCheckClientConfigProblems`: the startup rule `server/plugins/appCheckReadiness.ts` enforces.
 */

export const APP_CHECK_HEADER_NAME = 'X-Firebase-AppCheck'
export const APP_CHECK_REQUIRED_CODE = 'app_check_required'

/** `limited` = `getLimitedUseToken`, consumed server-side (`{ consume: true }`); one per request. */
export type AppCheckTokenKind = 'standard' | 'limited'

export interface AppCheckRoute {
  method: 'GET' | 'POST'
  path: string
  token: AppCheckTokenKind
}

export const APP_CHECK_ROUTES: readonly AppCheckRoute[] = [
  { method: 'GET', path: '/api/landing-research', token: 'standard' },
]

/** Which App Check token a request needs, or `null` for none. `method` defaults to GET. */
export function appCheckTokenKindFor(method: string | undefined, url: string): AppCheckTokenKind | null {
  const verb = (method || 'GET').toUpperCase()
  const path = url.split(/[?#]/, 1)[0]!.replace(/\/+$/, '') || '/'
  return APP_CHECK_ROUTES.find(r => r.method === verb && r.path === path)?.token ?? null
}

export interface AppCheckClientConfig {
  firebaseEmulator?: boolean
  recaptchaEnterpriseSiteKey?: string
  firebaseAppId?: string
}

/** `1:<project number>:web:<hex>` — what Firebase assigns a web app. */
const WEB_APP_ID = /^1:\d+:web:[0-9a-f]+$/

/**
 * What stops the browser from minting App Check tokens, given its public runtime config.
 *
 * Outside the emulator the server-side gate is always on (`verifyAppCheckToken`), so a client that
 * cannot mint tokens does not degrade: it locks every new account out of its first `users` row and
 * refuses every gated route. Hence a non-empty answer is a reason not to start.
 */
export function appCheckClientConfigProblems(config: AppCheckClientConfig): string[] {
  if (config.firebaseEmulator) return []
  const problems: string[] = []
  if (!config.recaptchaEnterpriseSiteKey?.trim()) {
    problems.push('NUXT_PUBLIC_RECAPTCHA_ENTERPRISE_SITE_KEY is not set: the browser cannot attest with reCAPTCHA Enterprise')
  }
  const appId = config.firebaseAppId?.trim() ?? ''
  if (!WEB_APP_ID.test(appId)) {
    problems.push(`NUXT_PUBLIC_FIREBASE_APP_ID is ${appId ? `malformed (${JSON.stringify(appId)})` : 'not set'}: the App Check token exchange has no web app to exchange against`)
  }
  return problems
}
